/**
 * TodoRow — one row of the unified Sales To Do list. Renders every kind:
 * offer chase, credit-approved docs chase, LoanDisk top-up / re-loan
 * prospect and diary task — with the right actions for each.
 */
import { useState } from "react";
import {
  Loader2, Plus, CheckCircle2, Ban, Trash2, ExternalLink, Phone, Mail,
  Send, Slack, PhoneCall, Copy, Flame, FileText, TrendingUp, RotateCcw,
  CalendarClock, UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { TASK_TYPE_META, ORDINAL } from "./SalesDialogs";

export const KIND_META = {
  chase:           { label: "Offer chase", icon: Flame, tone: "bg-orange-50 text-orange-700 border-orange-200", chip: "bg-orange-500" },
  credit_approved: { label: "Docs chase", icon: FileText, tone: "bg-pink-50 text-pink-700 border-pink-200", chip: "bg-pink-500" },
  topup:           { label: "Top-up", icon: TrendingUp, tone: "bg-emerald-50 text-emerald-700 border-emerald-200", chip: "bg-emerald-500" },
  reloan:          { label: "Re-loan", icon: RotateCcw, tone: "bg-sky-50 text-sky-700 border-sky-200", chip: "bg-sky-500" },
  task:            { label: "Task", icon: CheckCircle2, tone: "bg-slate-50 text-slate-700 border-slate-200", chip: "bg-slate-400" },
};
const PRIORITY_DOT = { red: "bg-red-600", amber: "bg-amber-400", green: "bg-emerald-500" };

function gbp(v) {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? `£${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "";
}
function shortDate(iso) {
  return iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
}

function AssignSelect({ item, users, me, onChanged }) {
  const [busy, setBusy] = useState(false);
  const assign = async (email) => {
    const value = email === "__none__" ? "" : email;
    const u = users.find(x => x.email === value);
    try {
      setBusy(true);
      if (item.kind === "task") {
        await api.patch(`/sales/tasks/${item.task_id}?updated_by=${encodeURIComponent(me)}`,
          { assigned_to: value, assigned_to_name: u?.name || "" });
      } else {
        await api.post(`/sales/todo/${item.kind}/${item.ref_id}/assign?updated_by=${encodeURIComponent(me)}`,
          { assignee: value, assignee_name: u?.name || "" });
      }
      toast.success(value ? `Assigned to ${u?.name || value}` : "Unassigned");
      await onChanged?.();
    } catch (e) {
      toast.error("Assign failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <Select value={item.assigned_to || "__none__"} onValueChange={assign} disabled={busy}>
      <SelectTrigger className="h-7 w-[120px] text-[11px]" data-testid={`scal-assign-${item.key}`}>
        <UserRound className="w-3 h-3 mr-0.5 shrink-0 text-slate-500" />
        <SelectValue placeholder="Assign…" />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        <SelectItem value="__none__" className="text-xs">Unassigned</SelectItem>
        {users.map(u => <SelectItem key={u.email} value={u.email} className="text-xs">{u.name || u.email}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function TodoRow({ item, me, users, statusOptions, onChanged, onOpenCard, onChase, onDial, onDiscuss, onAddTask, onSchedule, onOutcome }) {
  const [busy, setBusy] = useState(false);
  const meta = item.kind === "task"
    ? (TASK_TYPE_META[item.task_type] || TASK_TYPE_META.other)
    : KIND_META[item.kind];
  const Icon = meta.icon;
  const isMonday = item.kind === "chase" || item.kind === "credit_approved";
  const isProspect = item.kind === "topup" || item.kind === "reloan";
  const nc = item.next_chase;

  const changeStatus = async (newStatus) => {
    if (newStatus === item.status) return;
    try {
      setBusy(true);
      await api.put(`/applications/mirror/items/${item.item_id}/column`, { column_id: "status", value: newStatus });
      toast.success(`${item.title}: status → ${newStatus} (synced to Monday)`);
      await onChanged?.();
    } catch (e) {
      toast.error("Status change failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const logChased = async () => {
    try {
      setBusy(true);
      await api.post(`/sales/calendar/items/${item.item_id}/chased?chased_by=${encodeURIComponent(me)}`);
      toast.success(`${item.title} — manual chase logged (no email sent)`);
      await onChanged?.();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const setTaskStatus = async (status) => {
    try {
      setBusy(true);
      await api.patch(`/sales/tasks/${item.task_id}?updated_by=${encodeURIComponent(me)}`, { status });
      toast.success(status === "done" ? "Task completed" : "Task cancelled");
      await onChanged?.();
    } catch (e) {
      toast.error("Update failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  const removeTask = async () => {
    if (!window.confirm(`Delete task "${item.title}"?`)) return;
    try {
      setBusy(true);
      await api.delete(`/sales/tasks/${item.task_id}?deleted_by=${encodeURIComponent(me)}`);
      toast.success("Task deleted");
      await onChanged?.();
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const taskPrefill = () => onAddTask({
    deal_item_id: item.item_id || "", deal_name: isMonday ? item.title : (item.deal_name || item.title),
    sf_number: item.sf_number || "", contact_name: item.contact_name || "",
    contact_phone: item.contact_phone || "", contact_email: item.contact_email || "",
    title: `Call ${item.contact_name || item.title} back`,
  });

  return (
    <li className={`py-2.5 px-2 hover:bg-slate-50 ${item.kind === "task" && item.status !== "open" ? "opacity-60" : ""}`}
      data-testid={`scal-todo-${item.key}`}>
      <div className="flex items-start gap-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium inline-flex items-center gap-1 shrink-0 mt-0.5 ${meta.tone}`}>
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {item.kind === "chase" && (
              <span className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY_DOT[item.priority] || "bg-slate-300"}`}
                title={item.days_since_chase == null ? "Never chased" : `Chased ${item.days_since_chase}d ago`} />
            )}
            {isMonday ? (
              <button type="button" className="text-sm font-medium truncate hover:underline text-left"
                onClick={() => onOpenCard(item.item_id)} data-testid={`scal-open-card-${item.ref_id}`}>
                {item.title}
              </button>
            ) : (
              <span className={`text-sm font-medium truncate ${item.kind === "task" && item.status !== "open" ? "line-through text-slate-400" : ""}`}>
                {item.kind === "task" && item.time ? <span className="font-mono text-xs mr-1">{item.time}</span> : null}{item.title}
              </span>
            )}
            {isMonday && (
              <Badge variant="secondary" className={`text-[9px] px-1.5 ${item.kind === "credit_approved" ? "bg-pink-100 text-pink-700" : item.group === "Offer Accepted" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                {item.status || item.group}
              </Badge>
            )}
            {isProspect && item.dpd > 0 && (
              <Badge variant="secondary" className="text-[9px] px-1.5 bg-red-100 text-red-700">{item.dpd}d arrears</Badge>
            )}
            {item.snoozed && (
              <Badge variant="secondary" className="text-[9px] px-1.5 bg-amber-100 text-amber-800" title={item.snooze_note}>
                ⏰ {shortDate(item.due)}
              </Badge>
            )}
          </div>

          <div className="text-[11px] text-muted-foreground mt-0.5">
            {item.sf_number && <span className="font-mono">{item.sf_number} · </span>}
            {isMonday && item.offer_amount && <span>{gbp(item.offer_amount)} · </span>}
            {isMonday && <span>{item.broker || item.broker_direct || "—"}</span>}
            {isProspect && (
              <span>
                <span className="font-semibold text-slate-700">{item.pct_paid}% repaid</span>
                {" · "}{gbp(item.balance)} left of {gbp(item.total_due)}
              </span>
            )}
            {item.contact_name && <span> · {item.contact_name}</span>}
            {isProspect && item.contact_phone && <span> · {item.contact_phone}</span>}
            {item.kind === "task" && (item.deal_name || "") && <span> · {item.deal_name}</span>}
          </div>

          {item.kind === "chase" && (
            <div className="text-[10px] mt-0.5 flex items-center gap-2 flex-wrap">
              {item.days_since_chase == null
                ? <span className="text-red-700 font-semibold">Never chased</span>
                : item.days_since_chase === 0
                  ? <span className="text-emerald-700">Chased today</span>
                  : <span className={item.priority === "red" ? "text-red-700 font-semibold" : "text-amber-700"}>Last chased {item.days_since_chase}d ago</span>}
              {nc && (
                <span className={`font-semibold ${nc.overdue ? "text-red-700" : "text-orange-700"}`} data-testid={`scal-next-chase-${item.ref_id}`}>
                  {ORDINAL[nc.number]} chase {nc.overdue ? "DUE NOW" : `due ${shortDate(nc.due)}`}{nc.final ? " ⚠ final" : ""}
                </span>
              )}
            </div>
          )}
          {item.kind === "reloan" && (
            <div className="text-[10px] mt-0.5 text-sky-700 font-medium">Fully paid — prop for a new loan 🎉</div>
          )}
          {item.snooze_note && (
            <div className="text-[11px] text-amber-800 italic mt-0.5">&ldquo;{item.snooze_note}&rdquo;</div>
          )}
          {item.kind === "task" && item.notes && (
            <div className="text-[11px] text-slate-600 italic mt-0.5">&ldquo;{item.notes}&rdquo;</div>
          )}
          {item.kind === "task" && item.task_type === "ob_video_call" && (
            <div className="text-[10px] mt-0.5 text-indigo-700">
              {item.invite_sent ? "✉ Invite sent to customer" : item.invite_customer ? "Invite pending" : "No customer invite"}
              {item.video_link && <> · <a href={item.video_link} target="_blank" rel="noreferrer" className="underline">Join link</a></>}
            </div>
          )}
          {item.kind === "task" && item.contact_phone && item.task_type === "sms" && (
            <div className="text-[11px] text-slate-600 flex items-center gap-1.5 mt-0.5">
              <Phone className="w-2.5 h-2.5" /> {item.contact_phone}
              <a href={`sms:${item.contact_phone.replace(/\s/g, "")}`} className="text-emerald-700 underline">Text</a>
              <button type="button" className="text-slate-400 hover:text-slate-700" title="Copy number"
                onClick={() => { navigator.clipboard.writeText(item.contact_phone); toast.success("Number copied"); }}>
                <Copy className="w-2.5 h-2.5" />
              </button>
            </div>
          )}
          {isProspect && item.contact_email && (
            <div className="text-[11px] text-slate-600 flex items-center gap-1 mt-0.5">
              <Mail className="w-2.5 h-2.5" /> <a href={`mailto:${item.contact_email}`} className="underline">{item.contact_email}</a>
            </div>
          )}
          {(item.assigned_to_name || item.assigned_to) && (
            <div className="text-[10px] text-slate-500 mt-0.5">👤 {item.assigned_to_name || item.assigned_to}</div>
          )}
        </div>
        {isMonday && (
          <a href={item.url} target="_blank" rel="noreferrer" title="Open in Monday" className="shrink-0 text-slate-400 hover:text-slate-700 mt-0.5">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
        {item.kind === "chase" && (
          <>
            <Select value={item.status || undefined} onValueChange={changeStatus} disabled={busy}>
              <SelectTrigger className="h-7 w-[140px] text-[11px]" data-testid={`scal-status-${item.ref_id}`}>
                <SelectValue placeholder="Status…" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {nc && (
              <Button size="sm" className={`h-7 text-[11px] ${nc.final ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}`}
                onClick={() => onChase(item)} disabled={busy || nc.firing} data-testid={`scal-chase-${item.ref_id}`}
                title={nc.firing ? "A chase is already firing for this deal" : `Fire the ${ORDINAL[nc.number]} offer-chase email automation`}>
                {nc.firing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                {nc.firing ? "Firing…" : `Chase (${ORDINAL[nc.number]})`}
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={logChased} disabled={busy || item.days_since_chase === 0}
              data-testid={`scal-chased-${item.ref_id}`} title="Log a manual/phone chase — stamps today, no email">
              <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" /> Log
            </Button>
          </>
        )}

        {item.kind !== "task" && (
          <>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onDial(item)} title="Call via Aircall" data-testid={`scal-call-${item.key}`}>
              <PhoneCall className="w-3.5 h-3.5 text-blue-600" />
            </Button>
            {isMonday && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onDiscuss(item)} title="Discuss in Slack" data-testid={`scal-slack-${item.ref_id}`}>
                <Slack className="w-3.5 h-3.5 text-purple-600" />
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onSchedule(item)}
              data-testid={`scal-snooze-${item.key}`} title="Snooze / set the next action date with a note">
              <CalendarClock className="w-3 h-3 mr-1 text-emerald-700" /> {item.snoozed ? "Re-schedule" : "Next action"}
            </Button>
            {isProspect && (
              <Button size="sm" variant="outline" className="h-7 text-[11px] text-slate-700" onClick={() => onOutcome(item)} disabled={busy}
                data-testid={`scal-outcome-${item.key}`} title="Record the outcome (not interested / new app / no contact) — takes it off the To Do">
                <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" /> Handle
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-[11px]" data-testid={`scal-task-${item.key}`} onClick={taskPrefill}>
              <Plus className="w-3 h-3 mr-0.5" /> Task
            </Button>
          </>
        )}

        {item.kind === "task" && item.status === "open" && (
          <>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setTaskStatus("done")} disabled={busy} data-testid={`scal-task-done-${item.task_id}`}>
              <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" /> Done
            </Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onSchedule(item)} data-testid={`scal-task-move-${item.task_id}`} title="Edit due date">
              <CalendarClock className="w-3 h-3 mr-1 text-emerald-700" /> Move
            </Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setTaskStatus("cancelled")} disabled={busy}>
              <Ban className="w-3 h-3 mr-1 text-slate-500" /> Cancel
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-[11px] text-rose-600" onClick={removeTask} disabled={busy}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </>
        )}

        <div className="ml-auto">
          <AssignSelect item={item} users={users} me={me} onChanged={onChanged} />
        </div>
      </div>
    </li>
  );
}
